import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, MapPin, Calendar as CalendarIcon, Users, Loader2 } from "lucide-react";

export default function AcceptQuote() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobile, setMobile] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    let ok = true;
    api.get(`/bookings/${bookingId}`)
      .then(r => { if (ok) setBooking(r.data); })
      .catch(e => { if (ok) setError(e?.response?.data?.detail || "Booking not found"); })
      .finally(() => { if (ok) setLoading(false); });
    return () => { ok = false; };
  }, [bookingId]);

  const accept = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const r = await api.post(`/bookings/${bookingId}/accept-quote`, { mobile });
      setBooking(r.data.booking);
      setConfirmed(true);
      toast.success("Booking confirmed! Our team will assign a vehicle shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to confirm");
    }
    setSubmitting(false);
  };

  if (loading) return (
    <PublicLayout><section className="max-w-lg mx-auto px-4 pt-16 pb-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#ea580c]" /><div className="text-sm text-slate-500 mt-3">Loading your quote…</div></section></PublicLayout>
  );

  if (error) return (
    <PublicLayout><section className="max-w-lg mx-auto px-4 pt-16 pb-16 text-center" data-testid="accept-quote-error">
      <div className="p-6 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900">{error}</div>
      <Button onClick={() => nav("/")} className="mt-4 bg-[#1e1b4b]">Back to Home</Button>
    </section></PublicLayout>
  );

  if (confirmed || booking?.status === "confirmed") return (
    <PublicLayout>
      <section className="max-w-lg mx-auto px-4 pt-10 pb-16">
        <div className="p-8 rounded-2xl bg-white border border-emerald-200 shadow-sm text-center" data-testid="accept-quote-done">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <div className="text-emerald-700 text-xs uppercase tracking-widest font-bold mt-3">Confirmed</div>
          <h1 className="text-3xl font-display font-black tracking-tight mt-2">You're all set!</h1>
          <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200 text-left space-y-1 text-sm">
            <div><b>Booking:</b> <span className="font-mono text-xs">{booking.booking_id}</span></div>
            <div><b>Route:</b> {booking.from_place} → {booking.to_place}</div>
            <div><b>Date:</b> {booking.travel_date}</div>
            <div><b>Passengers:</b> {booking.passengers}</div>
            <div><b>Fare:</b> ₹{Number(booking.quoted_fare || booking.fare).toLocaleString()}</div>
          </div>
          <p className="text-slate-600 mt-4 text-sm">We'll WhatsApp your driver contact card 24 hours before departure.</p>
          <Button onClick={() => nav("/lookup")} className="mt-5 bg-[#ea580c] hover:bg-orange-700" data-testid="accept-quote-lookup">View my bookings</Button>
        </div>
      </section>
    </PublicLayout>
  );

  const quoted = Number(booking?.quoted_fare || 0);

  return (
    <PublicLayout>
      <section className="max-w-lg mx-auto px-4 pt-10 pb-16">
        <div className="text-xs uppercase tracking-widest text-[#ea580c] font-semibold">Confirm Your Quote</div>
        <h1 className="text-3xl sm:text-4xl font-display font-black tracking-tight">Accept & confirm the trip</h1>
        <p className="text-slate-600 mt-2 text-sm">Verify your mobile number to lock in the quoted fare.</p>

        <div className="mt-6 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4" data-testid="accept-quote-card">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-mono text-xs text-slate-500">{booking.booking_id}</div>
              <div className="font-display font-bold text-lg text-[#1e1b4b]">{booking.customer_name}</div>
            </div>
            {quoted > 0 && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Quoted</div>
                <div className="text-2xl font-black text-[#1e1b4b]">₹{quoted.toLocaleString()}</div>
              </div>
            )}
          </div>
          <div className="text-sm text-slate-700 space-y-1">
            <div className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#ea580c]" /> <b>{booking.from_place}</b> → <b>{booking.to_place}</b></div>
            <div className="flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5 text-slate-500" /> {booking.travel_date}{booking.departure_time && <span className="text-slate-500 text-xs">· {booking.departure_time}</span>}</div>
            <div className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-slate-500" /> {booking.passengers} pax · {booking.ac ? "AC" : "Non-AC"} · {booking.vehicle_type_pref || "Any vehicle"}</div>
          </div>
          {booking.quote_notes && (
            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-800">
              <b>Includes:</b> {booking.quote_notes}
            </div>
          )}
          {booking.status === "enquiry" && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-900">
              This booking is still awaiting a quote from our team.
            </div>
          )}
        </div>

        <form onSubmit={accept} className="mt-4 p-6 rounded-2xl bg-white border border-slate-200 space-y-4" data-testid="accept-quote-form">
          <div>
            <Label>Mobile number used at enquiry *</Label>
            <Input required inputMode="numeric" pattern="[0-9]*" maxLength={15} value={mobile} onChange={e => setMobile(e.target.value.replace(/[^0-9]/g, ""))} placeholder="e.g. 9876543210" data-testid="accept-quote-mobile" />
            <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> We verify the last 4 digits to protect your booking.</div>
          </div>
          <Button disabled={submitting || booking?.status !== "pending"} type="submit" className="w-full bg-[#ea580c] hover:bg-orange-700" data-testid="accept-quote-submit">
            {submitting ? "Confirming…" : `Accept & Confirm ${quoted > 0 ? `₹${quoted.toLocaleString()}` : ""}`}
          </Button>
          <p className="text-[11px] text-slate-500 text-center">Prefer to talk first? <a href="tel:+919890201047" className="text-[#ea580c] font-semibold">Call +91 98902 01047</a></p>
        </form>
      </section>
    </PublicLayout>
  );
}
